import { IconName, Icon, useClickOutside } from "@zapier/design-system";

import { Builder, RichText } from "@zapier/builder.io-kit";
import { Styles } from "./Popover.styles";
import { useRef, useState } from "react";
import { useFocusTrap } from "@/hooks";
import { emitUserInteractionEvent } from "@/observability/tracking/avroEventTracking/emitUserInteractionEvent";
import {
  EventAction,
  InteractionName,
} from "@/observability/tracking/avroEventTracking/trackingTypes";

const usePopover = ({
  triggerText,
  id,
}: {
  triggerText: string;
  id: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const focusTriggerElement = () => {
    setTimeout(() => triggerRef?.current?.focus(), 0);
  };

  const onOpen = () => {
    setIsOpen(true);

    emitUserInteractionEvent({
      event_action: EventAction.click,
      interaction_name: InteractionName.popover_opened,
      interaction_raw_text: triggerText,
      custom_tracking_fields: {
        popover_id: id,
      },
    });
  };

  const onClose = () => {
    setIsOpen(false);

    emitUserInteractionEvent({
      event_action: EventAction.click,
      interaction_name: InteractionName.popover_closed,

      custom_tracking_fields: {
        popover_id: id,
      },
    });

    focusTriggerElement();
  };

  const onOpenChange = (value: boolean) => {
    setIsOpen(value);
    value ? onOpen() : onClose();
  };

  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => (isOpen ? onOpenChange(false) : {}),
  });

  const { focusTrapRef } = useFocusTrap();

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      focusTriggerElement();
    }
  };

  const onClick = (
    event: React.MouseEvent<HTMLDivElement> & { target: HTMLDivElement }
  ) => {
    const anchorElement = event.target.closest("a");

    if (anchorElement) {
      emitUserInteractionEvent({
        event_action: EventAction.click,
        interaction_name: InteractionName.popover_link_clicked,
        interaction_raw_text: anchorElement?.textContent,
        destination_url_address: anchorElement?.getAttribute("href"),
        custom_tracking_fields: {
          popover_id: id,
        },
      });
    }
  };

  return {
    isOpen,
    triggerRef,
    onOpenChange,
    setClickOutsideNode,
    focusTrapRef,
    onKeyDown,
    onClick,
  };
};

type ModalProps = {
  children: React.ReactNode;
  id: string;
  trigger: {
    label: string;
    icon?: IconName;
  };

  content?: {
    title?: string;
    description?: string;
    linkLabel?: string;
    linkHref?: string;
    ariaLabel?: string;
  };
};

export default function Popover(props: ModalProps) {
  const {
    isOpen,
    onOpenChange,
    triggerRef,
    setClickOutsideNode,
    focusTrapRef,
    onKeyDown,
    onClick,
  } = usePopover({ triggerText: props.trigger.label, id: props.id });

  return (
    <div
      css={Styles.root}
      ref={(element) =>
        Builder.isEditing ? null : setClickOutsideNode(element)
      }
    >
      <button
        css={Styles.triggerButton}
        ref={triggerRef}
        onClick={() => onOpenChange(!isOpen)}
        aria-expanded={isOpen}
        {...(isOpen ? { "aria-controls": props.id } : {})}
      >
        {props.trigger?.icon ? (
          <Icon name={props.trigger?.icon as IconName} size={20} />
        ) : null}
        {props.trigger?.label ? props.trigger?.label : null}
      </button>
      {isOpen && (
        <div
          id={props.id}
          ref={(element) => (Builder.isEditing ? null : focusTrapRef(element))}
          css={Styles.content}
          aria-label={props.content?.ariaLabel}
          aria-modal="true"
          role="dialog"
          onKeyDownCapture={onKeyDown}
          onClickCapture={onClick}
          onAuxClickCapture={onClick}
        >
          {props.content && (
            <>
              <div css={Styles.textContainer}>
                {props.content?.title ? (
                  <h3 css={Styles.title}>{props.content?.title}</h3>
                ) : null}
                {props.content?.description ? (
                  <RichText html={props.content?.description} />
                ) : null}
              </div>
            </>
          )}
          {props.children}
          <button
            aria-label="Close dialog"
            css={Styles.closeButton}
            onClick={() => onOpenChange(false)}
          >
            <Icon isBlock={true} name="formX" size={24} />
          </button>
        </div>
      )}
    </div>
  );
}
